import * as React from 'react'
import { Button } from './button'

/**
 * The single, canonical empty-state for the whole app.
 *
 * Renders a themed illustration (a custom medallion built from theme tokens
 * only — no random colours) with a page icon, a title, a description and an
 * optional action button.
 *
 * Pass an icon component from the `react-ionicons` package, e.g.
 *   import { FolderOpenOutline } from 'react-ionicons'
 *   <EmptyState icon={FolderOpenOutline} ... />
 */
type IonIconComponent = React.ComponentType<{
  color?: string
  width?: string
  height?: string
  cssClasses?: string
}>

export interface EmptyStateProps {
  /** An outline icon component from `react-ionicons`. */
  icon: IonIconComponent
  title: string
  description: string
  actionLabel?: string
  onAction?: () => void
  className?: string
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  className = '',
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center px-6 py-16 text-center ${className}`}
    >
      {/* Themed illustration — primary colour + neutrals only */}
      <div style={{ position: 'relative', width: 176, height: 152, marginBottom: 20 }}>
        <svg
          viewBox="0 0 176 152"
          width="176"
          height="152"
          fill="none"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* dashed orbit ring */}
          <circle
            cx="88"
            cy="72"
            r="66"
            fill="none"
            stroke="var(--of-border)"
            strokeWidth="1.5"
            strokeDasharray="3 7"
          />
          {/* soft medallion — primary at 0.20 opacity */}
          <circle cx="88" cy="72" r="50" fill="var(--of-primary-20)" />
          {/* floating shapes */}
          <circle cx="22" cy="44" r="4" fill="var(--of-violet)" opacity="0.3" />
          <circle cx="158" cy="34" r="6.5" fill="var(--of-violet-soft)" />
          <circle cx="156" cy="116" r="3.5" fill="var(--of-violet)" opacity="0.35" />
          <circle cx="24" cy="118" r="5.5" fill="var(--of-border)" />
          {/* sparkle */}
          <path
            d="M150 64 C150 60 153 57 157 57 C153 57 150 54 150 50 C150 54 147 57 143 57 C147 57 150 60 150 64 Z"
            fill="var(--of-violet)"
            opacity="0.55"
          />
        </svg>
        {/* page icon centered on the medallion */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: 8,
          }}
        >
          <Icon color="var(--of-violet)" width="46px" height="46px" />
        </div>
      </div>

      <h3
        style={{
          fontSize: 17,
          fontWeight: 600,
          fontFamily: 'var(--of-font-body)',
          color: 'var(--of-fg-1)',
          margin: 0,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontSize: 13,
          lineHeight: 1.6,
          color: 'var(--of-fg-3)',
          margin: '6px 0 0',
          maxWidth: 340,
        }}
      >
        {description}
      </p>

      {actionLabel && onAction && (
        <Button onClick={onAction} size="sm" className="mt-5">
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
